import React, { useState } from 'react';
import { Lead } from '../../../types/lead.types';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { useContactLogger } from '../../../hooks/useContactLogger';
import {
  Plus,
  Phone,
  Mail,
  MessageSquare,
  User,
  CheckCircle,
  XCircle
} from 'lucide-react';

interface LeadContactsTabProps {
  lead: Lead;
  editedLead: Lead;
  setEditedLead: React.Dispatch<React.SetStateAction<Lead>>;
  onUpdate: (lead: Lead) => void;
  capturedByName: string;
}

const LeadContactsTab: React.FC<LeadContactsTabProps> = ({
  lead,
  editedLead,
  setEditedLead,
  onUpdate,
  capturedByName
}) => {
  const [showContactDialog, setShowContactDialog] = useState(false);

  // Shared logger hook (also used by the inline row in the spreadsheet view).
  // On save, it patches Firestore and we mirror the change into the locally
  // edited lead so the modal stays in sync without a refetch.
  // Pass `editedLead` (not `lead`) so a second save in the same modal
   // session sees the fresh contactAttempts list and appends to it instead
   // of overwriting the first save.
  const logger = useContactLogger(
    { id: editedLead.id, contactAttempts: editedLead.contactAttempts },
    {
    onSaved: (updatedAttempts) => {
      const updatedLead = {
        ...editedLead,
        contactAttempts: updatedAttempts,
        lastContacted: Timestamp.now(),
      };
      setEditedLead(updatedLead);
      onUpdate(updatedLead);
      setShowContactDialog(false);
    },
  }
  );

  const handleAddContact = () => {
    logger.save();
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'phone': return <Phone className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'text': return <MessageSquare className="w-4 h-4" />;
      case 'in-person': return <User className="w-4 h-4" />;
      default: return <Phone className="w-4 h-4" />;
    }
  };

  const getMethodLabel = (method: string) => {
    switch (method) {
      case 'phone': return 'Phone Call';
      case 'email': return 'Email';
      case 'text': return 'Text Message';
      case 'in-person': return 'In Person';
      default: return method;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Contact History</h3>
        <button
          onClick={() => setShowContactDialog(true)}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          <Plus className="w-4 h-4" />
          Log Contact
        </button>
      </div>

      {/* Initial capture as first contact */}
      <div className="space-y-3">
        {/* Show lead capture as first contact */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-100 rounded-full">
              <User className="w-4 h-4 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="font-medium text-green-800">Lead Captured (In Person)</span>
                <span className="text-xs text-green-600">
                  {editedLead.createdAt ? format(editedLead.createdAt.toDate(), 'MMM d, yyyy h:mm a') : 'Unknown'}
                </span>
              </div>
              <p className="text-sm text-green-700 mt-1">
                Initial contact - Lead captured by {capturedByName || 'Unknown'}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-xs text-green-600">Successful</span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact attempts list */}
        {editedLead.contactAttempts && editedLead.contactAttempts.length > 0 ? (
          [...editedLead.contactAttempts]
            .sort((a, b) => {
              const dateA = a.timestamp?.toDate?.() || new Date(0);
              const dateB = b.timestamp?.toDate?.() || new Date(0);
              return dateB.getTime() - dateA.getTime();
            })
            .map((contact, index) => (
              <div
                key={index}
                className={`border rounded-lg p-4 ${contact.successful ? 'bg-white border-gray-200' : 'bg-red-50 border-red-200'}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-full ${contact.successful ? 'bg-gray-100' : 'bg-red-100'}`}>
                    {getMethodIcon(contact.method)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{getMethodLabel(contact.method)}</span>
                      <span className="text-xs text-gray-500">
                        {contact.timestamp?.toDate ? format(contact.timestamp.toDate(), 'MMM d, yyyy h:mm a') : 'Unknown'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{contact.notes}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-500">by {contact.userName || 'Unknown'}</span>
                      <div className="flex items-center gap-2">
                        {contact.successful ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-xs text-green-600">Successful</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 text-red-600" />
                            <span className="text-xs text-red-600">Unsuccessful</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
        ) : (
          <p className="text-gray-500 text-sm">No additional contacts logged yet.</p>
        )}
      </div>

      {/* Add Contact Dialog */}
      {showContactDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowContactDialog(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Log Contact Attempt</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Method</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'phone', label: 'Phone', icon: <Phone className="w-4 h-4" /> },
                    { value: 'email', label: 'Email', icon: <Mail className="w-4 h-4" /> },
                    { value: 'text', label: 'Text', icon: <MessageSquare className="w-4 h-4" /> },
                    { value: 'in-person', label: 'In Person', icon: <User className="w-4 h-4" /> }
                  ].map((method) => (
                    <button
                      key={method.value}
                      type="button"
                      onClick={() => logger.setMethod(method.value as any)}
                      className={`flex items-center gap-2 px-3 py-2 border rounded-md text-sm ${
                        logger.method === method.value
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {method.icon}
                      {method.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Was the contact successful?</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={logger.successful}
                      onChange={() => logger.setSuccessful(true)}
                      className="h-4 w-4 text-indigo-600"
                    />
                    <span className="text-sm text-gray-700">Yes, made contact</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={!logger.successful}
                      onChange={() => logger.setSuccessful(false)}
                      className="h-4 w-4 text-indigo-600"
                    />
                    <span className="text-sm text-gray-700">No answer/response</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={logger.notes}
                  onChange={(e) => logger.setNotes(e.target.value)}
                  rows={3}
                  placeholder="Describe the contact attempt..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowContactDialog(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                disabled={logger.saving}
              >
                Cancel
              </button>
              <button
                onClick={handleAddContact}
                disabled={logger.saving || !logger.notes.trim()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {logger.saving ? 'Saving...' : 'Log Contact'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadContactsTab;
